import type {
  Can2faResponse,
  DangNhap2faInput,
  LoginInput,
  MeResponse,
  ModuleId,
  PermissionLevel,
  RequiredLevel,
  YoutubeMode,
} from '@qlk/shared';
import { canBuocHai, hasPermission } from '@qlk/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { LoiApi, api } from './api.js';

const KHOA_ME = ['core', 'me'] as const;

export function useToi() {
  return useQuery<MeResponse>({
    queryKey: KHOA_ME,
    queryFn: () => api.get<MeResponse>('/api/v1/core/me'),
    retry: (soLan, err) => !(err instanceof LoiApi && err.status === 401) && soLan < 2,
    staleTime: 60_000,
  });
}

/**
 * Bước một: email và mật khẩu.
 *
 * Đáp án là **một trong hai**: `MeResponse` (vào được ngay) hoặc
 * `Can2faResponse` (tài khoản đã bật 2FA — còn bước hai, và lượt này chưa có
 * cookie phiên nào). `canBuocHai` phân biệt hai dạng.
 *
 * Chỉ ghi vào cache `me` khi **thật sự** đã đăng nhập: nhồi một
 * `Can2faResponse` vào đó là `CanDangNhap` tưởng đã xong rồi dựng app với dữ
 * liệu rỗng.
 */
export function useDangNhap() {
  const qc = useQueryClient();
  return useMutation<MeResponse | Can2faResponse, LoiApi, LoginInput>({
    mutationFn: (input) => api.post<MeResponse | Can2faResponse>('/api/v1/core/auth/login', input),
    onSuccess: (du) => {
      if (!canBuocHai(du)) qc.setQueryData(KHOA_ME, du);
    },
  });
}

/** Bước hai: mã 6 số của app xác thực, hoặc một mã dự phòng. */
export function useDangNhapBuocHai() {
  const qc = useQueryClient();
  return useMutation<MeResponse, LoiApi, DangNhap2faInput>({
    mutationFn: (input) => api.post<MeResponse>('/api/v1/core/auth/2fa', input),
    onSuccess: (du) => qc.setQueryData(KHOA_ME, du),
  });
}

export function useDangXuat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: boolean }>('/api/v1/core/auth/logout'),
    onSettled: () => qc.clear(),
  });
}

/**
 * Nhãn "Dữ liệu mẫu" ở eyebrow **chỉ hiện khi máy chủ chạy `YOUTUBE_MODE=mock`**.
 *
 * Trả về đường dẫn breadcrumb của trang: `['Vận hành', 'Dữ liệu mẫu']` ở mock,
 * `['Vận hành']` ở live. Chưa tải xong `/core/me` thì **không** gắn nhãn —
 * thà thiếu một nhịp còn hơn nháy chữ "Dữ liệu mẫu" trên dữ liệu thật.
 */
export function duongDanTrang(nhom: string, youtubeMode: YoutubeMode | undefined): string[] {
  return youtubeMode === 'mock' ? [nhom, NHAN_DU_LIEU_MAU] : [nhom];
}

export const NHAN_DU_LIEU_MAU = 'Dữ liệu mẫu';

/** `duongDanTrang` cho trang đang mở, tự đọc chế độ từ `/core/me`. */
export function useDuongDanTrang(nhom: string): string[] {
  const toi = useToi();
  return duongDanTrang(nhom, toi.data?.youtubeMode);
}

/**
 * Người đang xem **bị giới hạn theo kênh được giao mà chưa được giao kênh nào**.
 *
 * Đây là trạng thái của thành viên vừa được mời: mọi trang số liệu sẽ rỗng, và
 * rỗng vì một lý do rất cụ thể. Không phân biệt được thì màn hình trông y hệt
 * "công ty chưa có dữ liệu", và người mới ngồi chờ một thứ không bao giờ tới.
 *
 * Trả `false` khi chưa tải xong `/core/me`: thà thiếu một nhịp còn hơn nháy
 * khối hướng dẫn rồi giật mất.
 */
export function useChuaCoKenh(): boolean {
  const toi = useToi();
  return toi.data?.assignedChannelIds?.length === 0;
}

/**
 * Người đang xem **bị giới hạn theo kênh được giao** hay không.
 *
 * Trả thêm `daBiet` vì câu trả lời "chưa biết" là một trạng thái thật và phải
 * dùng được: trang Doanh thu chọn giữa hai truy vấn loại trừ nhau
 * (`/overview` cho người toàn công ty, `/tong-quan-kenh` cho người bị giới
 * hạn), nên **không được gọi cái nào** trước khi `/core/me` xong. Đoán sai một
 * nhịp nghĩa là nháy đúng khung đỏ 403 mà bản vá này đang dọn.
 *
 * Không suy từ `assignedChannelIds === null`: hai thứ đó hiện trùng nhau,
 * nhưng cái quyết định phép lọc ở máy chủ là `phamViDuLieu` (đợt 17/10/2026),
 * nên giao diện đọc đúng cột đó.
 */
export function usePhamViDuLieu(): { biGioiHanKenh: boolean; daBiet: boolean } {
  const toi = useToi();
  const phamVi = toi.data?.phamViDuLieu;

  return { biGioiHanKenh: phamVi === 'kenh_duoc_giao', daBiet: phamVi !== undefined };
}

/**
 * Người đang đăng nhập có **xem được số tiền** hay không (`revenue:view`).
 *
 * Rà soát bảo mật 31/10/2026, mục **S5**. Máy chủ **bỏ hẳn** mọi trường tiền
 * khỏi đáp án khi thiếu quyền này (`@qlk/shared/locTien.ts`), nên giao diện
 * phải có một nhánh riêng: ẩn cột, ẩn ô, ẩn thanh, ẩn lựa chọn sắp theo tiền.
 * Thiếu nhánh đó thì trang hiện một dải `—` mà không ai biết vì sao — đúng bài
 * học của đợt 6: *một mức quyền đặt được mà không có màn hình dùng được đi kèm
 * thì mức quyền đó = "mất trang"*.
 *
 * `daBiet` để trang không nhấp nháy: trong lúc chờ `GET /core/me` thì chưa
 * biết, và đoán **theo phía an toàn** (không có tiền) là đúng — hiện cột rồi
 * rút đi còn tệ hơn.
 */
export function useXemTien(): { coXemTien: boolean; daBiet: boolean } {
  const toi = useToi();
  return {
    coXemTien: coQuyen(toi.data?.permissions, 'revenue', 'view'),
    daBiet: toi.data !== undefined,
  };
}

/** Kiểm tra quyền ở phía giao diện (máy chủ vẫn kiểm tra lại). */
export function coQuyen(
  permissions: Record<string, PermissionLevel> | undefined,
  module: ModuleId,
  muc: RequiredLevel = 'view',
): boolean {
  const level = permissions?.[module];
  return level !== undefined && hasPermission(level, muc);
}
